import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { DatePreferencesProvider } from './hooks/useDatePreferences'
import { LoadingProvider } from './contexts/LoadingContext'
import GlobalLoadingIndicator from './components/GlobalLoadingIndicator'
import { ToastProvider } from './components/Toast'
import { ConfirmProvider } from './components/ConfirmModal'
import Layout from './components/Layout'
import Login from './pages/Login'
import MagicLinkVerify from './pages/MagicLinkVerify'
import EmailVerify from './pages/EmailVerify'
import Dashboard from './pages/Dashboard'
import ExecutiveDashboard from './pages/ExecutiveDashboard'
import Controls from './pages/Controls'
import ControlDetail from './pages/ControlDetail'
import ControlNew from './pages/ControlNew'
import Evidence from './pages/Evidence'
import Users from './pages/Users'
import Risks from './pages/Risks'
import Frameworks from './pages/Frameworks'
import FrameworkDashboard from './pages/FrameworkDashboard'
import ActivityLog from './pages/ActivityLog'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Integrations from './pages/Integrations'
import Documents from './pages/Documents'
import TPRM from './pages/TPRM'
import DarkWeb from './pages/DarkWeb'
import Tasks from './pages/Tasks'
import ControlMappings from './pages/ControlMappings'
import Scanners from './pages/Scanners'
import Assets from './pages/Assets'
import Help from './pages/Help'
import CustomFields from './pages/CustomFields'
import KRIs from './pages/KRIs'
import GapAnalysis from './pages/GapAnalysis'
import Permissions from './pages/Permissions'
import PublicQuestionnaire from './pages/PublicQuestionnaire'
import RiskAppetite from './pages/RiskAppetite'
import InternalControls from './pages/InternalControls'
import Training from './pages/Training'
import TrainingModuleDetail from './pages/TrainingModuleDetail'
import TrainingCampaignDetail from './pages/TrainingCampaignDetail'
import Phishing from './pages/Phishing'
import PhishingCampaignDetail from './pages/PhishingCampaignDetail'
import PhishingTemplateEditor from './pages/PhishingTemplateEditor'
import Diagrams from './pages/Diagrams'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Layout>{children}</Layout>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/verify/:token"
        element={
          <PublicRoute>
            <MagicLinkVerify />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/verify-email/:token"
        element={
          <PublicRoute>
            <EmailVerify />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/executive"
        element={
          <ProtectedRoute>
            <ExecutiveDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controls"
        element={
          <ProtectedRoute>
            <Controls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controls/new"
        element={
          <ProtectedRoute>
            <ControlNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controls/:id"
        element={
          <ProtectedRoute>
            <ControlDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal-controls"
        element={
          <ProtectedRoute>
            <InternalControls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/evidence"
        element={
          <ProtectedRoute>
            <Evidence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risks"
        element={
          <ProtectedRoute>
            <Risks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-appetite"
        element={
          <ProtectedRoute>
            <RiskAppetite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/frameworks"
        element={
          <ProtectedRoute>
            <Frameworks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/frameworks/:frameworkId/dashboard"
        element={
          <ProtectedRoute>
            <FrameworkDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/framework/:frameworkId"
        element={
          <ProtectedRoute>
            <FrameworkDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-log"
        element={
          <ProtectedRoute>
            <ActivityLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/gap-analysis"
        element={
          <ProtectedRoute>
            <GapAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/permissions"
        element={
          <ProtectedRoute>
            <Permissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tprm"
        element={
          <ProtectedRoute>
            <TPRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dark-web"
        element={
          <ProtectedRoute>
            <DarkWeb />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mappings"
        element={
          <ProtectedRoute>
            <ControlMappings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scanners"
        element={
          <ProtectedRoute>
            <Scanners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute>
            <Assets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields"
        element={
          <ProtectedRoute>
            <CustomFields />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kris"
        element={
          <ProtectedRoute>
            <KRIs />
          </ProtectedRoute>
        }
      />
      <Route path="/questionnaire/:token" element={<PublicQuestionnaire />} />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <Training />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/modules/:id"
        element={
          <ProtectedRoute>
            <TrainingModuleDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/campaigns/:id"
        element={
          <ProtectedRoute>
            <TrainingCampaignDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phishing"
        element={
          <ProtectedRoute>
            <Phishing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phishing/campaigns/:id"
        element={
          <ProtectedRoute>
            <PhishingCampaignDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phishing/templates/:id"
        element={
          <ProtectedRoute>
            <PhishingTemplateEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diagrams"
        element={
          <ProtectedRoute>
            <Diagrams />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <DatePreferencesProvider>
        <LoadingProvider>
          <ToastProvider>
            <ConfirmProvider>
              <GlobalLoadingIndicator />
              <AppRoutes />
            </ConfirmProvider>
          </ToastProvider>
        </LoadingProvider>
      </DatePreferencesProvider>
    </AuthProvider>
  )
}
